/**
 * AssessmentView — formal assessment for trainees
 *
 * Loads questions from cert_assessment_questions (fixed mode) or random selection.
 * Shows all questions, collects answers, submits for scoring.
 * Timer if time_limit_minutes is set.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

interface ContentQuestion {
  id: string;
  content_code: string;
  module_title?: string;
  question_text: string;
  option_a?: string;
  option_b?: string;
  option_c?: string;
  option_d?: string;
  correct_option?: string;
}

interface AssessmentData {
  id: string;
  name: string;
  description?: string;
  passing_score: number;
  time_limit_minutes?: number | null;
  mode: string;
  total_questions: number;
}

interface AssessmentViewProps {
  assessmentId: string;
  onBack: () => void;
  accentColor?: string;
}

function AssessmentView({ assessmentId, onBack, accentColor = '#772cbb' }: AssessmentViewProps) {
  const { supabase, user } = useAuth();
  const [assessment, setAssessment] = useState<AssessmentData | null>(null);
  const [questions, setQuestions] = useState<ContentQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadAssessment();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once
  }, [assessmentId]);

  const handleSubmit = useCallback(async () => {
    if (submitted) return;

    // Score it
    let correct = 0;
    questions.forEach(q => {
      if (answers[q.id] === q.correct_option) correct++;
    });
    const pct = questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0;
    const didPass = pct >= (assessment?.passing_score || 80);

    setScore(pct);
    setPassed(didPass);
    setSubmitted(true);

    if (timerRef.current) clearInterval(timerRef.current);

    // Log to cert_training_log
    if (user?.id) {
      await supabase.from('cert_training_log').insert({
        operator_id: user.id,
        event_type: 'assessment_complete',
        assessment_id: assessmentId,
        score: pct,
        passed: didPass,
        created_at: new Date().toISOString(),
      });
    }
  }, [submitted, questions, answers, assessment, user, supabase, assessmentId]);

  useEffect(() => {
    if (timeRemaining !== null && timeRemaining <= 0 && !submitted) {
      handleSubmit();
    }
  }, [timeRemaining, submitted, handleSubmit]);

  async function loadAssessment() {
    setLoading(true);

    const { data: assessData } = await supabase
      .from('cert_assessments')
      .select('id, name, description, passing_score, time_limit_minutes, mode, total_questions')
      .eq('id', assessmentId)
      .single();

    if (!assessData) { setLoading(false); return; }
    setAssessment(assessData as AssessmentData);

    let questionItems: ContentQuestion[] = [];

    if (assessData.mode === 'fixed') {
      // Load linked questions in order
      const { data: links } = await supabase
        .from('cert_assessment_questions')
        .select('content_id, display_order')
        .eq('assessment_id', assessmentId)
        .order('display_order', { ascending: true });

      const contentIds = (links || []).map((l: { content_id: string }) => l.content_id);
      if (contentIds.length > 0) {
        const { data: items } = await supabase
          .from('cert_training_content')
          .select('id, content_code, module_title, question_text, option_a, option_b, option_c, option_d, correct_option')
          .in('id', contentIds);

        const allItems = (items || []) as ContentQuestion[];
        questionItems = contentIds
          .map((id: string) => allItems.find(item => item.id === id))
          .filter((item): item is ContentQuestion => !!item && !!item.question_text);
      }
    } else {
      // Random: pick total_questions from all active question content
      const { data: allQuestions } = await supabase
        .from('cert_training_content')
        .select('id, content_code, module_title, question_text, option_a, option_b, option_c, option_d, correct_option')
        .eq('active', true)
        .not('question_text', 'is', null);

      const pool = (allQuestions || []) as ContentQuestion[];
      const count = Math.min(assessData.total_questions, pool.length);
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      questionItems = shuffled.slice(0, count);
    }

    setQuestions(questionItems);

    // Start timer if applicable
    if (assessData.time_limit_minutes) {
      const seconds = assessData.time_limit_minutes * 60;
      setTimeRemaining(seconds);
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => (prev !== null ? prev - 1 : null));
      }, 1000);
    }

    setLoading(false);
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>Loading assessment...</div>;
  }

  if (!assessment) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>Assessment not found.</div>;
  }

  if (submitted) {
    return (
      <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center', padding: '2rem 0' }}>
        <div style={{
          width: '80px', height: '80px', borderRadius: '50%', margin: '0 auto 1.5rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 'var(--text-5xl)',
          background: passed ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
          border: `3px solid ${passed ? 'var(--color-success)' : 'var(--color-error)'}`,
        }}>
          {passed ? '\u2713' : '\u2717'}
        </div>

        <h2 style={{ margin: '0 0 0.5rem', fontSize: 'var(--text-4xl)', fontWeight: 600 }}>
          {passed ? 'Congratulations!' : 'Not quite there'}
        </h2>
        <p style={{ color: 'var(--color-text-light)', marginBottom: '1.5rem' }}>
          You scored <strong>{score}%</strong> (pass mark: {assessment.passing_score}%).
        </p>

        <div className="flex justify-center gap-md">
          <button onClick={onBack} style={{
            padding: '0.625rem 1.5rem', borderRadius: 'var(--radius)',
            border: '1px solid #e5e7eb', background: 'var(--color-white)',
            fontSize: 'var(--text-base)', fontWeight: 500, cursor: 'pointer',
          }}>
            Back to Course
          </button>
          {!passed && (
            <button
              onClick={() => {
                setSubmitted(false);
                setAnswers({});
                setScore(0);
                loadAssessment();
              }}
              style={{
                padding: '0.625rem 1.5rem', borderRadius: 'var(--radius)',
                border: 'none', background: accentColor, color: 'var(--color-white)',
                fontSize: 'var(--text-base)', fontWeight: 500, cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === questions.length;

  return (
    <div>
      <button onClick={onBack} style={backStyle}>&larr; Back</button>

      {/* Assessment header */}
      <div style={{
        background: 'var(--color-white)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem',
        border: '1px solid #e5e7eb', marginBottom: '1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.5rem',
      }}>
        <div>
          <h2 className="text-lg fw-600 m-0">{assessment.name}</h2>
          <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)', marginTop: '4px' }}>
            {questions.length} questions &middot; Pass mark: {assessment.passing_score}%
          </div>
        </div>

        {timeRemaining !== null && (
          <div style={{
            padding: '0.5rem 1rem', borderRadius: 'var(--radius)', fontWeight: 600,
            fontSize: 'var(--text-xl)', fontFamily: 'monospace',
            background: timeRemaining < 60 ? 'var(--color-error-bg)' : 'var(--color-bg)',
            color: timeRemaining < 60 ? 'var(--color-error)' : 'var(--color-sidebar-hover)',
          }}>
            {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
          </div>
        )}
      </div>

      {/* Questions */}
      <div className="flex flex-col gap-xl">
        {questions.map((q, qIndex) => (
          <div key={q.id} style={{
            background: 'var(--color-white)', borderRadius: 'var(--radius-lg)', padding: '1.5rem',
            border: '1px solid #e5e7eb',
          }}>
            <div style={{ fontWeight: 500, marginBottom: '1rem', lineHeight: 1.5 }}>
              <span style={{ color: 'var(--color-text-light)', marginRight: '0.5rem' }}>{qIndex + 1}.</span>
              {q.question_text}
            </div>

            {['A', 'B', 'C', 'D'].map(opt => {
              const text = q[`option_${opt.toLowerCase()}` as keyof ContentQuestion] as string;
              if (!text) return null;
              const isSelected = answers[q.id] === opt;

              return (
                <div
                  key={opt}
                  onClick={() => setAnswers({ ...answers, [q.id]: opt })}
                  style={{
                    padding: '0.625rem 0.875rem', borderRadius: 'var(--radius)',
                    border: `2px solid ${isSelected ? accentColor : '#e5e7eb'}`,
                    background: isSelected ? `${accentColor}08` : '#fff',
                    marginBottom: '0.375rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.625rem',
                    transition: 'all 0.12s',
                  }}
                >
                  <span style={{
                    width: '24px', height: '24px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 'var(--text-xs)', fontWeight: 600, flexShrink: 0,
                    background: isSelected ? accentColor : 'var(--color-bg)',
                    color: isSelected ? 'var(--color-white)' : 'var(--color-text-light)',
                  }}>
                    {opt}
                  </span>
                  <span className="text-sm">{text}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Submit bar */}
      <div style={{
        marginTop: '2rem', padding: '1rem 1.5rem',
        background: 'var(--color-white)', borderRadius: 'var(--radius-lg)',
        border: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>
          {answeredCount} of {questions.length} answered
        </span>
        <button
          onClick={handleSubmit}
          disabled={!allAnswered}
          style={{
            padding: '0.625rem 2rem', borderRadius: 'var(--radius)', border: 'none',
            background: allAnswered ? accentColor : 'var(--color-border)',
            color: allAnswered ? 'var(--color-white)' : 'var(--color-text-light)',
            fontSize: 'var(--text-lg)', fontWeight: 600,
            cursor: allAnswered ? 'pointer' : 'default',
          }}
        >
          Submit Assessment
        </button>
      </div>
    </div>
  );
}

const backStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-light)', fontSize: 'var(--text-base)', padding: '0 0 1rem',
  display: 'flex', alignItems: 'center', gap: '6px',
};

export default AssessmentView;
